/**
 * The job page's device dialogs: edit a device, and add one to the job (pick
 * from the site's devices, or quick-add a new one). Moved out of detail.tsx
 * unchanged (11/3) so the new job page uses the very same dialogs.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { SIZE_OPTIONS } from "@/lib/assembly-sizes";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { LocationPickerMap } from "@/components/location-picker-map";
import { assetVisual } from "@/lib/asset-visual";
import { computeFireSafetyName } from "@/lib/fire-safety-name";
import { SiteDeviceList, fetchAllSiteAssets } from "./site-device-list";
import { DeviceConflictPanel, parseDeviceConflict, type DeviceConflict } from "@/components/duplicate-device-conflict";
import { apiErrorInfo, assetAllowedOnTrade, familyForTrade, type JobAssetRow } from "./types";

const SERVES_LABELS: Record<string, string> = {
    fireline: "Fireline",
    fireline_bypass: "Fireline Bypass",
    irrigation: "Irrigation",
    domestic_mainline: "Domestic Mainline",
    domestic_other: "Domestic-Other",
    other: "Other",
};

// ──────────────────────────────────────────────────────────────────────────
// Devices (job_assets) panel with add/remove
// Two ways to add: (1) pick from existing site assets (typical follow-up
// after a Vepo/Swift account lookup), or (2) quick-add a brand new assembly.
// ──────────────────────────────────────────────────────────────────────────

const ASSEMBLY_TYPES = [
    { value: "rpz", label: "RPZ" },
    { value: "dc", label: "DC" },
    { value: "dcda", label: "DCDA" },
    { value: "rpda", label: "RPDA" },
    { value: "pvb", label: "PVB" },
    { value: "svb", label: "SVB" },
];

const SERVICE_TYPES = [
    { value: "fireline", label: "Fireline" },
    { value: "fireline_bypass", label: "Fireline Bypass" },
    { value: "irrigation", label: "Irrigation" },
    { value: "domestic_mainline", label: "Domestic Mainline" },
    { value: "domestic_other", label: "Domestic-Other" },
    { value: "other", label: "Other" },
];

export function EditDeviceModal({
    jobId,
    siteId,
    asset,
    onClose,
}: {
    jobId: string;
    siteId: string | null;
    asset: JobAssetRow;
    onClose: () => void;
}) {
    const qc = useQueryClient();

    const [name, setName] = useState(asset.label ?? "");
    const [sizeLabel, setSizeLabel] = useState(asset.size_label ?? "");
    const [serialNumber, setSerialNumber] = useState(asset.serial_number ?? "");
    const [manufacturer, setManufacturer] = useState(asset.manufacturer ?? "");
    const [model, setModel] = useState(asset.model ?? "");
    const [assemblyType, setAssemblyType] = useState(asset.assembly_type ?? "");
    const [serviceType, setServiceType] = useState(asset.service_type ?? "");
    const [serviceTypeOther, setServiceTypeOther] = useState(asset.service_type_other ?? "");
    const [meterNumber, setMeterNumber] = useState(asset.meter_number ?? "");
    const [locationDescription, setLocationDescription] = useState(asset.location_description ?? "");
    const [parentAssetId, setParentAssetId] = useState(asset.parent_asset_id ?? "");

    const needsMeter = assemblyType === "dcda" || assemblyType === "rpda";
    // A detector main can't itself be someone's bypass; the select is
    // for the DC/RPZ legs (and legacy rows with no type yet).
    const canBeBypass = !needsMeter;

    // "Link to parent" candidates: the site's top-level assemblies,
    // DCDA/RPDA first (they're the ones that own a bypass leg).
    const siteAssets = useQuery({
        queryKey: ["site-assets", siteId],
        queryFn: () => fetchAllSiteAssets(siteId ?? ""),
        enabled: !!siteId && canBeBypass,
    });
    const parentOptions = (siteAssets.data?.assets ?? [])
        .filter((a) => a.id !== asset.asset_id && !a.parent_asset_id)
        .sort((a, b) => {
            const aDet = a.assembly_type === "dcda" || a.assembly_type === "rpda" ? 0 : 1;
            const bDet = b.assembly_type === "dcda" || b.assembly_type === "rpda" ? 0 : 1;
            return aDet - bDet || (a.name ?? "").localeCompare(b.name ?? "");
        });

    function buildPatch() {
        const body: Record<string, unknown> = {};
        const str = (val: string) => val.trim() || null;
        if (name.trim() !== (asset.label ?? "")) body.name = name.trim() || undefined;
        if (sizeLabel !== (asset.size_label ?? "")) body.sizeLabel = str(sizeLabel);
        if (serialNumber !== (asset.serial_number ?? "")) body.serialNumber = str(serialNumber);
        if (manufacturer !== (asset.manufacturer ?? "")) body.manufacturer = str(manufacturer);
        if (model !== (asset.model ?? "")) body.model = str(model);
        if (assemblyType !== (asset.assembly_type ?? "")) body.assemblyType = str(assemblyType);
        if (serviceType !== (asset.service_type ?? "")) body.serviceType = str(serviceType);
        if (serviceTypeOther !== (asset.service_type_other ?? "")) body.serviceTypeOther = str(serviceTypeOther);
        if (meterNumber !== (asset.meter_number ?? "")) body.meterNumber = str(meterNumber);
        if (locationDescription !== (asset.location_description ?? ""))
            body.locationDescription = str(locationDescription);
        if (parentAssetId !== (asset.parent_asset_id ?? "")) body.parentAssetId = parentAssetId || null;
        return body;
    }

    const save = useMutation({
        mutationFn: () => api.patch(`/api/assets/${asset.asset_id}`, buildPatch()),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            if (siteId) qc.invalidateQueries({ queryKey: ["site-assets", siteId] });
            toast.success("Device updated");
            onClose();
        },
        onError: (err: unknown) => {
            const info = apiErrorInfo(err);
            toast.error(info.message ? `Couldn't update device: ${info.message}` : "Couldn't update device");
        },
    });

    const hasChanges = Object.keys(buildPatch()).length > 0;

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="w-full max-w-xl rounded-lg border bg-card shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h3 className="text-sm font-semibold">Edit device</h3>
                    <button type="button" onClick={onClose} className="text-foreground/80 hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <form
                    className="p-4 space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        save.mutate();
                    }}
                >
                    <div>
                        <label className="text-xs font-medium">Name / label</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} />
                    </div>

                    {/* Row 1: Type + Size */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">Type</label>
                            <select
                                value={assemblyType}
                                onChange={(e) => setAssemblyType(e.target.value)}
                                className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                            >
                                <option value="">— None —</option>
                                {ASSEMBLY_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-medium">Size</label>
                            <select
                                value={sizeLabel}
                                onChange={(e) => setSizeLabel(e.target.value)}
                                className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                            >
                                <option value="">— None —</option>
                                {SIZE_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Row 2: Serial + Serves */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">Serial #</label>
                            <Input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Serves</label>
                            <select
                                value={serviceType}
                                onChange={(e) => setServiceType(e.target.value)}
                                className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                            >
                                <option value="">— None —</option>
                                {SERVICE_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {serviceType === "domestic_other" || serviceType === "other" ? (
                        <div>
                            <label className="text-xs font-medium">Describe what it serves</label>
                            <Input value={serviceTypeOther} onChange={(e) => setServiceTypeOther(e.target.value)} />
                        </div>
                    ) : null}

                    {/* Row 3: Make + Model */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">Make</label>
                            <Input
                                value={manufacturer}
                                onChange={(e) => setManufacturer(e.target.value)}
                                placeholder="e.g. Watts, Wilkins"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Model</label>
                            <Input
                                value={model}
                                onChange={(e) => setModel(e.target.value)}
                                placeholder="e.g. 007, 350"
                            />
                        </div>
                    </div>

                    {/* Meter # — only for DCDA / RPDA */}
                    {needsMeter && (
                        <div>
                            <label className="text-xs font-medium">Meter #</label>
                            <Input value={meterNumber} onChange={(e) => setMeterNumber(e.target.value)} />
                        </div>
                    )}

                    <div>
                        <label className="text-xs font-medium">Location on property</label>
                        <Input
                            value={locationDescription}
                            onChange={(e) => setLocationDescription(e.target.value)}
                            placeholder="e.g. Vault at driveway, Mechanical room"
                        />
                    </div>

                    {/* Bypass ↔ parent is a real relationship, not location
                        text — link the DC/RPZ leg to its DCDA/RPDA main here. */}
                    {canBeBypass && siteId && (
                        <div>
                            <label className="text-xs font-medium">Link to parent (bypass of)</label>
                            <select
                                value={parentAssetId}
                                onChange={(e) => setParentAssetId(e.target.value)}
                                className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                            >
                                <option value="">— Not a bypass —</option>
                                {parentOptions.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                        {p.serial_number ? ` · S/N ${p.serial_number}` : ""}
                                        {p.assembly_type ? ` (${p.assembly_type.toUpperCase()})` : ""}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-[11px] text-foreground/70">
                                Links this device as the bypass leg of a DCDA/RPDA assembly — they render grouped and
                                test together.
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" size="sm" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" size="sm" disabled={save.isPending || !hasChanges}>
                            {save.isPending ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export function AddDevicePicker({
    jobId,
    siteId,
    tradeCode,
    siteLat,
    siteLng,
    currentTab,
    onTabChange,
    linkedAssetIds,
    onClose,
}: {
    jobId: string;
    siteId: string;
    tradeCode: string | null;
    siteLat: number | null;
    siteLng: number | null;
    currentTab: "existing" | "quick";
    onTabChange: (t: "existing" | "quick") => void;
    linkedAssetIds: Set<string>;
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const { isField } = useAuth();

    // Always fetch site assets while the dialog is open: the existing
    // tab needs the list to render, the quick-add tab needs the count
    // by family for the "Will be saved as #N" preview to be accurate
    // even when the user lands on Quick-add first.
    const siteAssets = useQuery({
        queryKey: ["site-assets", siteId],
        queryFn: () => fetchAllSiteAssets(siteId),
    });

    const linkMultiple = useMutation({
        mutationFn: async (assetIds: string[]) => {
            // Attempt every selection — one bad device must not block the
            // rest of the site from being added. Every non-2xx is
            // accounted for in the toast (Prompt 21 3b: no silent
            // failures — a swallowed 409 once made "N added" a lie).
            let added = 0;
            let alreadyLinked = 0;
            let firstError: unknown = null;
            let failed = 0;
            for (const assetId of assetIds) {
                try {
                    await api.post(`/api/jobs/${jobId}/assets`, { assetId });
                    added++;
                } catch (err: unknown) {
                    const code = apiErrorInfo(err).error ?? (err instanceof Error ? err.message : "");
                    if (code === "ALREADY_LINKED") {
                        alreadyLinked++;
                        continue;
                    }
                    failed++;
                    if (!firstError) firstError = err;
                }
            }
            if (firstError) throw Object.assign(firstError as object, { failedCount: failed, addedCount: added });
            return { added, alreadyLinked };
        },
        onSuccess: ({ added, alreadyLinked }) => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            const parts: string[] = [];
            if (added > 0) parts.push(`${added} device${added === 1 ? "" : "s"} added to job`);
            if (alreadyLinked > 0) {
                parts.push(
                    `${alreadyLinked} ${alreadyLinked === 1 ? "was" : "were"} already on this job — check the Devices list`,
                );
            }
            const msg = parts.join("; ") || "Nothing to add";
            if (added > 0) toast.success(msg);
            else toast.info(msg);
            onClose();
        },
        onError: (err: unknown) => {
            // Some devices may have linked before the failure — refresh so
            // the job reflects them, then explain what went wrong.
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            const info = apiErrorInfo(err);
            const failed = (err as { failedCount?: number }).failedCount;
            const added = (err as { addedCount?: number }).addedCount ?? 0;
            const prefix = failed && failed > 1 ? `${failed} devices couldn't be added` : "Couldn't add device";
            const suffix = added > 0 ? ` (${added} added before the failure)` : "";
            toast.error((info.message ? `${prefix}: ${info.message}` : prefix) + suffix);
        },
    });

    const [qName, setQName] = useState("");
    const [qServes, setQServes] = useState("");
    const [qServesOther, setQServesOther] = useState("");
    const [qType, setQType] = useState("");
    const [qSize, setQSize] = useState("");
    const [qSerial, setQSerial] = useState("");
    const [qMake, setQMake] = useState("");
    const [qModel, setQModel] = useState("");
    const [qLoc, setQLoc] = useState("");
    const [qMeter, setQMeter] = useState("");
    // Map pin — REQUIRED only for field users (they're standing at the
    // device). Office adds may skip it: the server defaults coords to the
    // site's with location_pin_status='unset' and the first tech on site
    // confirms the real pin (supersedes PRE-CUTOVER #44 for office adds).
    const [qPinLat, setQPinLat] = useState<number | null>(null);
    const [qPinLng, setQPinLng] = useState<number | null>(null);
    const [qCreateBypass, setQCreateBypass] = useState(true);
    const [qBypassSerial, setQBypassSerial] = useState("");
    const [qBypassMake, setQBypassMake] = useState("");
    const [qBypassModel, setQBypassModel] = useState("");

    // Fire-safety quick-add state. We mirror the field workflow's
    // descriptors (extinguisher class + capacity, or e-light type) so
    // the office quick-add yields the same auto-named asset rows the
    // tech would produce on the truck.
    const [fsAssetType, setFsAssetType] = useState<"extinguisher" | "e_light">("extinguisher");
    const [fsLocation, setFsLocation] = useState("");
    const [fsClass, setFsClass] = useState("");
    const [fsCapacityValue, setFsCapacityValue] = useState("");
    const [fsCapacityUnit, setFsCapacityUnit] = useState<"lb" | "gal" | "kg">("lb");
    const [fsLightType, setFsLightType] = useState("emergency");
    const [fsPinLat, setFsPinLat] = useState<number | null>(null);
    const [fsPinLng, setFsPinLng] = useState<number | null>(null);

    // "Will be saved as" preview — uses the existing-on-site count + 1
    // as a sequence hint. Real numbering is server-side, but the
    // preview lets the user see the eventual shape before tapping Save.
    const fsExistingCount = (siteAssets.data?.assets ?? []).filter((a) => a.asset_kind === fsAssetType).length;
    const fsPreviewName = computeFireSafetyName(
        fsAssetType,
        {
            extinguisher_class: fsClass || undefined,
            capacity_value: fsCapacityValue || undefined,
            capacity_unit: fsCapacityValue ? fsCapacityUnit : undefined,
            light_type: fsLightType || undefined,
        },
        fsExistingCount + 1,
    );

    const needsMeter = qType === "dcda" || qType === "rpda";
    const needsBypass = needsMeter;
    const bypassType = qType === "dcda" ? "DC" : qType === "rpda" ? "RPZ" : "";

    const cascadeType = qType?.startsWith("dcda") ? "dc" : qType?.startsWith("rpda") ? "rpz" : qType || "";
    const cascade = useQuery({
        queryKey: ["device-cascade", cascadeType, qSize, qMake],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (cascadeType) params.set("assemblyType", cascadeType);
            if (qSize) params.set("size", qSize);
            if (qMake) params.set("manufacturer", qMake);
            return api.get<{ sizes: string[]; manufacturers: string[]; models: string[] }>(
                `/api/device-cascade?${params}`,
            );
        },
        staleTime: 60_000,
        // Only fire once an assembly type is chosen. Without one the server
        // returns 400 ("assemblyType required"), so firing it on every
        // Quick-add open — including fire-safety adds, which never have an
        // assembly type — was a guaranteed broken call. cascadeType is empty
        // until a backflow type is picked, and always empty for extinguishers.
        enabled: currentTab === "quick" && cascadeType !== "",
    });
    const cascadeSizes = cascade.data?.sizes ?? [];
    const cascadeManufacturers = cascade.data?.manufacturers ?? [];
    const cascadeModels = cascade.data?.models ?? [];

    const ASSEMBLY_TYPE_LABEL: Record<string, string> = {
        rpz: "RPZ",
        dc: "DC",
        dcda: "DCDA",
        rpda: "RPDA",
        pvb: "PVB",
        svb: "SVB",
    };

    // Prompt 22 2c: duplicate-serial / possible-duplicate 409 from
    // quick-create renders inline with [Use existing device] instead of
    // a dead-end toast.
    const [quickConflict, setQuickConflict] = useState<DeviceConflict | null>(null);

    const quickCreate = useMutation({
        mutationFn: ({ allowDuplicate = false }: { allowDuplicate?: boolean } = {}) => {
            let name = qName.trim();
            if (!name) {
                const typePart = qType ? (ASSEMBLY_TYPE_LABEL[qType] ?? qType.toUpperCase()) : "";
                const sizePart = qSize || "";
                const servesPart = qServes && qServes !== "other" ? (SERVES_LABELS[qServes] ?? "") : "";
                name = [typePart, sizePart, servesPart].filter(Boolean).join(" ") || "Asset";
            }
            return api.post(`/api/jobs/${jobId}/assets/quick-create`, {
                name,
                serves: qServes || undefined,
                servesOther: qServes === "other" ? qServesOther.trim() || undefined : undefined,
                assemblyType: qType || undefined,
                sizeLabel: qSize || undefined,
                serialNumber: qSerial.trim() || undefined,
                manufacturer: qMake.trim() || undefined,
                model: qModel.trim() || undefined,
                locationDescription: qLoc.trim() || undefined,
                meterNumber: needsMeter ? qMeter.trim() || undefined : undefined,
                locationLat: qPinLat,
                locationLng: qPinLng,
                createBypass: needsBypass ? qCreateBypass : undefined,
                // Bypass on a DCDA/RPDA is always the same size as the main
                // assembly — see docs/PRE-CUTOVER-UX-FEEDBACK.md #19. The UI
                // no longer asks for it; mirror qSize to keep the server
                // contract identical.
                bypassSize: needsBypass && qCreateBypass ? qSize || undefined : undefined,
                bypassSerialNumber: needsBypass && qCreateBypass ? qBypassSerial.trim() || undefined : undefined,
                bypassManufacturer: needsBypass && qCreateBypass ? qBypassMake.trim() || undefined : undefined,
                bypassModel: needsBypass && qCreateBypass ? qBypassModel.trim() || undefined : undefined,
                allowDuplicate: allowDuplicate || undefined,
            });
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["site-assets", siteId] });
            const msg =
                needsBypass && qCreateBypass
                    ? `Device + bypass ${bypassType} created and added`
                    : "Device created and added";
            toast.success(msg);
            onClose();
        },
        onError: (e) => {
            const dup = parseDeviceConflict(e);
            if (dup) {
                setQuickConflict(dup);
                return;
            }
            const info = apiErrorInfo(e);
            toast.error(info.message ? `Couldn't create device: ${info.message}` : "Couldn't create device");
        },
    });

    // Fire-safety quick-add posts to the dedicated `/api/fire-safety/asset`
    // endpoint so the server's auto-namer + family_seq_no logic kicks in.
    // The office user never sees / types a name — descriptors + the
    // sequence number compose the saved label.
    const fireSafetyQuickCreate = useMutation({
        mutationFn: () => {
            const meta: Record<string, string> = {};
            if (fsAssetType === "extinguisher") {
                if (fsClass) meta.extinguisher_class = fsClass;
                if (fsCapacityValue) {
                    meta.capacity_value = fsCapacityValue;
                    meta.capacity_unit = fsCapacityUnit;
                    if (fsCapacityUnit === "lb") meta.capacity_lbs = fsCapacityValue;
                }
            } else {
                if (fsLightType) meta.light_type = fsLightType;
            }
            return api.post(`/api/fire-safety/asset`, {
                jobId,
                assetType: fsAssetType,
                locationDescription: fsLocation.trim() || undefined,
                locationLat: fsPinLat,
                locationLng: fsPinLng,
                complianceMetadata: meta,
            });
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["site-assets", siteId] });
            toast.success("Device created and added");
            onClose();
        },
        onError: () => toast.error("Couldn't create device"),
    });

    // Filter to the asset families this trade actually services so a
    // backflow job's picker doesn't surface extinguishers (and vice
    // versa). When the trade is unknown / generic we leave the list
    // unfiltered to preserve historical behavior. Note: site-assets
    // returns `asset_type` aliased as `asset_kind`.
    const family = familyForTrade(tradeCode);
    // Same predicate the Devices table render uses — picker and list
    // must agree on what belongs, or a device can be addable yet never
    // visibly appear on the job.
    // Every device on the site for this trade, INCLUDING ones already on the
    // job — those render greyed with "on this job" instead of vanishing.
    const tradeAssets = (siteAssets.data?.assets ?? []).filter((a) => assetAllowedOnTrade(tradeCode, a.asset_kind));
    const availableAssets = tradeAssets.filter((a) => !linkedAssetIds.has(a.id));

    // Don't strand the user on an empty "From this site" tab. When the site
    // has no unlinked assets to pick (common on fire-safety jobs whose only
    // extinguisher is already on the job), that tab is a dead-end message —
    // so land them on Quick-add, where they can actually create a device.
    // One-shot (ref-guarded) so a deliberate click back to "From this site"
    // isn't immediately bounced away.
    const autoSwitchedToQuick = useRef(false);
    useEffect(() => {
        if (
            !autoSwitchedToQuick.current &&
            currentTab === "existing" &&
            !siteAssets.isLoading &&
            availableAssets.length === 0
        ) {
            autoSwitchedToQuick.current = true;
            onTabChange("quick");
        }
    }, [siteAssets.isLoading, availableAssets.length, currentTab]);

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-2 sm:p-4" onClick={onClose}>
            {/* Height-capped flex column: header + tabs stay put, the body
                scrolls. Without the cap a long list or the Quick-add form ran
                off the bottom of a laptop/phone screen with no way down. */}
            <div
                role="dialog"
                aria-label="Add device to job"
                className="flex max-h-[calc(100dvh-1rem)] w-full max-w-xl flex-col overflow-hidden rounded-lg border bg-card shadow-xl sm:max-h-[90dvh]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
                    <h3 className="text-sm font-semibold">Add device to job</h3>
                    <button type="button" onClick={onClose} className="rounded p-1 text-foreground/80 hover:bg-muted">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="flex shrink-0 border-b text-sm">
                    <button
                        type="button"
                        className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                            currentTab === "existing"
                                ? "border-primary text-foreground"
                                : "border-transparent text-foreground/80 hover:text-foreground"
                        }`}
                        onClick={() => onTabChange("existing")}
                    >
                        From this site
                    </button>
                    <button
                        type="button"
                        className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                            currentTab === "quick"
                                ? "border-primary text-foreground"
                                : "border-transparent text-foreground/80 hover:text-foreground"
                        }`}
                        onClick={() => onTabChange("quick")}
                    >
                        Quick-add new
                    </button>
                </div>
                {currentTab === "existing" ? (
                    siteAssets.isLoading ? (
                        <div className="flex justify-center py-6">
                            <Loader2 className="h-5 w-5 animate-spin text-foreground/80" />
                        </div>
                    ) : availableAssets.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 p-4 py-6 text-center">
                            <p className="text-sm text-foreground/80">
                                {tradeAssets.length > 0
                                    ? `All ${tradeAssets.length} device${tradeAssets.length === 1 ? " is" : "s are"} already on this job.`
                                    : "No existing devices left to add from this site."}
                            </p>
                            <Button size="sm" onClick={() => onTabChange("quick")}>
                                <Plus className="mr-1.5 h-4 w-4" />
                                Quick-add a new device
                            </Button>
                        </div>
                    ) : (
                        <SiteDeviceList
                            assets={tradeAssets}
                            linkedAssetIds={linkedAssetIds}
                            pending={linkMultiple.isPending}
                            onAdd={(ids) => linkMultiple.mutate(ids)}
                        />
                    )
                ) : (
                    <div className="min-h-0 flex-1 overflow-y-auto p-4">
                        {family === "fire_ext" ? (
                            // Fire-safety quick-add: Type / Location pin /
                            // descriptors. No Name input — the server's
                            // fireSafetyName helper composes the canonical
                            // label from the descriptors below.
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    if (!fsLocation.trim()) {
                                        toast.error("Location is required");
                                        return;
                                    }
                                    // Pin is OPTIONAL for office adds — staff
                                    // transcribing a WO list aren't on site. The
                                    // server defaults coords to the site's with
                                    // location_pin_status='unset' and the first
                                    // tech on site confirms the real pin.
                                    fireSafetyQuickCreate.mutate();
                                }}
                                className="space-y-3"
                            >
                                <div>
                                    <label className="text-xs font-medium">Type</label>
                                    <div className="mt-1 grid grid-cols-2 gap-2">
                                        {(["extinguisher", "e_light"] as const).map((t) => {
                                            const v = assetVisual(t);
                                            const Icon = v.Icon;
                                            const active = fsAssetType === t;
                                            return (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    onClick={() => setFsAssetType(t)}
                                                    className={cn(
                                                        "flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors",
                                                        active
                                                            ? cn(v.badgeClassName, "border-current font-semibold")
                                                            : "border-border text-foreground/80 hover:bg-muted/50",
                                                    )}
                                                >
                                                    <Icon className="h-4 w-4" />
                                                    {t === "extinguisher" ? "Extinguisher" : "Emergency Light"}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-medium">
                                        Location <span className="text-destructive">*</span>
                                    </label>
                                    <Input
                                        placeholder='e.g. "Lobby — North Wall"'
                                        value={fsLocation}
                                        onChange={(e) => setFsLocation(e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="text-xs font-medium block mb-1.5">
                                        Where is it on the property?{" "}
                                        <span className="text-muted-foreground font-normal">(optional)</span>
                                    </label>
                                    <LocationPickerMap
                                        centerLat={siteLat}
                                        centerLng={siteLng}
                                        pinLat={fsPinLat}
                                        pinLng={fsPinLng}
                                        onPinChange={(lat, lng) => {
                                            setFsPinLat(lat);
                                            setFsPinLng(lng);
                                        }}
                                    />
                                    {(fsPinLat == null || fsPinLng == null) && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            No pin? No problem — the first tech on site will drop it.
                                        </p>
                                    )}
                                </div>

                                {fsAssetType === "extinguisher" ? (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-xs font-medium">Class</label>
                                            <select
                                                value={fsClass}
                                                onChange={(e) => setFsClass(e.target.value)}
                                                className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                                            >
                                                <option value="">— Select —</option>
                                                {["ABC", "BC", "CO2", "K", "water", "halotron"].map((c) => (
                                                    <option key={c} value={c}>
                                                        {c}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium">Capacity</label>
                                            <div className="mt-1 flex gap-2">
                                                <Input
                                                    inputMode="decimal"
                                                    placeholder="e.g. 5"
                                                    value={fsCapacityValue}
                                                    onChange={(e) => setFsCapacityValue(e.target.value)}
                                                />
                                                <select
                                                    value={fsCapacityUnit}
                                                    onChange={(e) =>
                                                        setFsCapacityUnit(e.target.value as "lb" | "gal" | "kg")
                                                    }
                                                    className="rounded-lg border bg-background px-2 py-2 text-sm"
                                                >
                                                    <option value="lb">lb</option>
                                                    <option value="gal">gal</option>
                                                    <option value="kg">kg</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <label className="text-xs font-medium">Light Type</label>
                                        <select
                                            value={fsLightType}
                                            onChange={(e) => setFsLightType(e.target.value)}
                                            className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                                        >
                                            {["emergency", "exit", "combo"].map((c) => (
                                                <option key={c} value={c}>
                                                    {c}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <p className="text-xs text-foreground/80 bg-muted/50 rounded-md px-3 py-2">
                                    Will be saved as: <span className="font-mono">{fsPreviewName}</span>
                                </p>

                                <div className="flex items-center gap-2 pt-1">
                                    <Button type="submit" size="sm" disabled={fireSafetyQuickCreate.isPending}>
                                        {fireSafetyQuickCreate.isPending ? "Adding…" : "Create & add"}
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={onClose}
                                        disabled={fireSafetyQuickCreate.isPending}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        ) : (
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    if (isField && !qServes) {
                                        toast.error("Serves is required");
                                        return;
                                    }
                                    // PIN REQUIRED ONLY IN THE FIELD — a tech is
                                    // standing at the device, so they must drop
                                    // the pin. Office staff transcribing a WO
                                    // list can't know where it sits: the server
                                    // defaults coords to the site's with
                                    // location_pin_status='unset' and the first
                                    // tech on site confirms the real pin.
                                    if (isField && (qPinLat == null || qPinLng == null)) {
                                        toast.error("Drop a pin on the map so this device shows up on the route");
                                        return;
                                    }
                                    setQuickConflict(null);
                                    quickCreate.mutate({});
                                }}
                                className="space-y-3"
                            >
                                {/* Row 1: Serves + Type */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-medium">
                                            Serves{isField && <span className="text-destructive"> *</span>}
                                        </label>
                                        <select
                                            value={qServes}
                                            onChange={(e) => setQServes(e.target.value)}
                                            className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                                            required={isField}
                                        >
                                            <option value="">— Select —</option>
                                            <option value="fireline">Fireline</option>
                                            <option value="fireline_bypass">Fireline Bypass</option>
                                            <option value="irrigation">Irrigation</option>
                                            <option value="domestic_mainline">Domestic Mainline</option>
                                            <option value="domestic_other">Domestic-Other</option>
                                            <option value="other">Other…</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium">Type</label>
                                        <select
                                            value={qType}
                                            onChange={(e) => setQType(e.target.value)}
                                            className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                                        >
                                            <option value="">— Select —</option>
                                            <option value="rpz">RPZ</option>
                                            <option value="dc">DC</option>
                                            <option value="dcda">DCDA</option>
                                            <option value="rpda">RPDA</option>
                                            <option value="pvb">PVB</option>
                                            <option value="svb">SVB</option>
                                        </select>
                                    </div>
                                </div>

                                {qServes === "other" && (
                                    <div>
                                        <label className="text-xs font-medium">Describe what it serves</label>
                                        <Input
                                            placeholder="e.g. Pool fill line, cooling tower"
                                            value={qServesOther}
                                            onChange={(e) => setQServesOther(e.target.value)}
                                        />
                                    </div>
                                )}

                                {/* Row 2: Size + Serial */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-medium">Size</label>
                                        <Input
                                            list="qa-sizes"
                                            placeholder='e.g. 2"'
                                            value={qSize}
                                            onChange={(e) => setQSize(e.target.value)}
                                        />
                                        <datalist id="qa-sizes">
                                            {cascadeSizes.map((s) => (
                                                <option key={s} value={s} />
                                            ))}
                                        </datalist>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium">Serial #</label>
                                        <Input value={qSerial} onChange={(e) => setQSerial(e.target.value)} />
                                    </div>
                                </div>

                                {/* Row 3: Make + Model (cascade-driven) */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-medium">Make</label>
                                        <Input
                                            list="qa-makes"
                                            placeholder="e.g. Watts, Wilkins"
                                            value={qMake}
                                            onChange={(e) => setQMake(e.target.value)}
                                        />
                                        <datalist id="qa-makes">
                                            {cascadeManufacturers.map((m) => (
                                                <option key={m} value={m} />
                                            ))}
                                        </datalist>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium">Model</label>
                                        <Input
                                            list="qa-models"
                                            placeholder="e.g. LF007M2, 975XL"
                                            value={qModel}
                                            onChange={(e) => setQModel(e.target.value)}
                                        />
                                        <datalist id="qa-models">
                                            {cascadeModels.map((m) => (
                                                <option key={m} value={m} />
                                            ))}
                                        </datalist>
                                    </div>
                                </div>

                                {/* Meter number — only for DCDA / RPDA */}
                                {needsMeter && (
                                    <div>
                                        <label className="text-xs font-medium">Meter #</label>
                                        <Input
                                            placeholder="Water meter number"
                                            value={qMeter}
                                            onChange={(e) => setQMeter(e.target.value)}
                                        />
                                    </div>
                                )}

                                {/* Bypass section — only for DCDA / RPDA */}
                                {needsBypass && (
                                    <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                                        <label className="flex items-center gap-2 text-xs font-medium">
                                            <input
                                                type="checkbox"
                                                checked={qCreateBypass}
                                                onChange={(e) => setQCreateBypass(e.target.checked)}
                                                className="rounded"
                                            />
                                            Also create bypass {bypassType}
                                        </label>
                                        {qCreateBypass && (
                                            <div className="grid grid-cols-2 gap-3 pt-1">
                                                {/* Per docs/PRE-CUTOVER-UX-FEEDBACK.md #19 — the
                                                bypass on a DCDA / RPDA is always the same
                                                physical size as the main assembly, so the office
                                                quick-add no longer exposes a separate "Bypass
                                                size" input. The submit path below mirrors the
                                                main `qSize` into `bypassSize` so the server
                                                contract stays unchanged. */}
                                                <div>
                                                    <label className="text-xs font-medium text-foreground/80">
                                                        Bypass serial #
                                                    </label>
                                                    <Input
                                                        value={qBypassSerial}
                                                        onChange={(e) => setQBypassSerial(e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-medium text-foreground/80">
                                                        Bypass make
                                                    </label>
                                                    <Input
                                                        value={qBypassMake}
                                                        onChange={(e) => setQBypassMake(e.target.value)}
                                                        placeholder="e.g. Watts, Wilkins"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-medium text-foreground/80">
                                                        Bypass model
                                                    </label>
                                                    <Input
                                                        value={qBypassModel}
                                                        onChange={(e) => setQBypassModel(e.target.value)}
                                                        placeholder="e.g. 007, 350"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <p className="text-[11px] text-foreground/80">
                                            The bypass {bypassType} is its own asset linked to the main{" "}
                                            {qType.toUpperCase()}. Both will be added to this job.
                                        </p>
                                    </div>
                                )}

                                <div>
                                    <label className="text-xs font-medium">Location on property</label>
                                    <Input
                                        placeholder="e.g. East side of building, near main gate"
                                        value={qLoc}
                                        onChange={(e) => setQLoc(e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="text-xs font-medium block mb-1.5">
                                        Where is it on the property?{" "}
                                        {isField ? (
                                            <span className="text-destructive">*</span>
                                        ) : (
                                            <span className="text-muted-foreground font-normal">(optional)</span>
                                        )}
                                    </label>
                                    <LocationPickerMap
                                        centerLat={siteLat}
                                        centerLng={siteLng}
                                        pinLat={qPinLat}
                                        pinLng={qPinLng}
                                        onPinChange={(lat, lng) => {
                                            setQPinLat(lat);
                                            setQPinLng(lng);
                                        }}
                                    />
                                    {qPinLat == null || qPinLng == null ? (
                                        isField ? (
                                            <p className="text-xs text-destructive mt-1">
                                                Tap the map to place a pin — location is required so it shows up on the
                                                dispatch route
                                            </p>
                                        ) : (
                                            <p className="text-xs text-muted-foreground mt-1">
                                                No pin? No problem — the first tech on site will drop it.
                                            </p>
                                        )
                                    ) : (
                                        <p className="text-xs text-foreground/70 mt-1">
                                            Drag the pin to fine-tune. Saved with the device so it appears on the map.
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="text-xs font-medium">Name / label</label>
                                    <Input
                                        placeholder={
                                            qType && qSize
                                                ? `Auto: ${ASSEMBLY_TYPE_LABEL[qType] ?? qType.toUpperCase()} ${qSize}`
                                                : 'e.g. RPZ 2" — Back parking lot'
                                        }
                                        value={qName}
                                        onChange={(e) => setQName(e.target.value)}
                                    />
                                    <p className="mt-0.5 text-[11px] text-foreground/80">
                                        Optional. Auto-generated from type + size if left blank.
                                    </p>
                                </div>

                                {quickConflict && (
                                    <DeviceConflictPanel
                                        conflict={quickConflict}
                                        onUseExisting={(asset) => {
                                            setQuickConflict(null);
                                            linkMultiple.mutate([asset.id]);
                                        }}
                                        onCreateAnyway={
                                            quickConflict.kind === "possible"
                                                ? () => quickCreate.mutate({ allowDuplicate: true })
                                                : undefined
                                        }
                                        createAnywayPending={quickCreate.isPending}
                                    />
                                )}

                                <div className="flex items-center gap-2 pt-1">
                                    <Button type="submit" size="sm" disabled={quickCreate.isPending}>
                                        {quickCreate.isPending
                                            ? "Adding…"
                                            : needsBypass && qCreateBypass
                                              ? "Create both & add"
                                              : "Create & add"}
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={onClose}
                                        disabled={quickCreate.isPending}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
